import React, { useEffect } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import MetaverseCanvas from '../components/MetaverseCanvas';
import AnnouncementBar from '../components/AnnouncementBar';
import './Home.css';

// 优先使用后台上传的视频（/uploads），带时间戳避免浏览器缓存旧文件；
// 后端不可用时回退到构建自带的 /assets 版本
const VIDEO_SRC = `/uploads/metaverse.mp4?v=${Date.now()}`;

const Home: React.FC = () => {
  const { t } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="home-page">
      <AnnouncementBar />
      <section id="hero">
        <MetaverseCanvas />
        <div className="hero-video-container">
          <video autoPlay muted loop playsInline>
            <source src={VIDEO_SRC} type="video/mp4" />
            <source src="/assets/metaverse.mp4" type="video/mp4" />
          </video>
        </div>
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="hero-badge">
            <span className="pulse"></span>
            <span>{t.hero.badge}</span>
          </div>
          <h1 className="hero-title">
            <span className="gradient-text">METACREATION</span>
            <br />
            SPACE LAB
          </h1>
          <p className="hero-subtitle-cn">{t.hero.subtitle}</p>
          <p className="hero-desc">{t.hero.desc}</p>
          <div className="hero-buttons">
            <button className="btn-primary" onClick={() => window.location.href = '/ecosystem'}>
              {t.hero.btn1}
            </button>
            <button className="btn-secondary" onClick={() => window.location.href = '/about'}>
              {t.hero.btn2}
            </button>
          </div>
          <div className="hero-stats">
            <div className="hero-stat">
              <div className="num">8</div>
              <div className="label">{t.hero.stat1}</div>
            </div>
            <div className="hero-stat">
              <div className="num">4</div>
              <div className="label">{t.hero.stat2}</div>
            </div>
            <div className="hero-stat">
              <div className="num">50+</div>
              <div className="label">{t.hero.stat3}</div>
            </div>
            <div className="hero-stat">
              <div className="num">100%</div>
              <div className="label">{t.hero.stat4}</div>
            </div>
          </div>
        </div>
      </section>

      <section id="about">
        <div className="section-header">
          <div className="section-tag">{t.about.tag}</div>
          <h2 className="section-title">
            <span>{t.about.heading1}</span>{' '}
            <span className="gradient-text">{t.about.heading2}</span>
            {t.about.heading3 && <span>{t.about.heading3}</span>}
          </h2>
          <p className="section-subtitle">{t.about.p1}</p>
        </div>
        <div className="about-grid">
          <div className="about-visual">
            <div className="about-video-container">
              <video autoPlay muted loop playsInline>
                <source src={VIDEO_SRC} type="video/mp4" />
                <source src="/assets/metaverse.mp4" type="video/mp4" />
              </video>
            </div>
          </div>
          <div className="about-text">
            <h3>
              <span>{t.about.heading1}</span>{' '}
              <span className="gradient-text">{t.about.heading2}</span>
              {t.about.heading3 && <span>{t.about.heading3}</span>}
            </h3>
            <p>{t.about.p1}</p>
            <p>{t.about.p2}</p>
            <div className="about-features">
              <div className="about-feature">
                <div className="icon" style={{ background: 'rgba(0,229,255,0.15)', color: '#00e5ff' }}>◆</div>
                <span>{t.about.f1}</span>
              </div>
              <div className="about-feature">
                <div className="icon" style={{ background: 'rgba(240,192,64,0.12)', color: '#f0c040' }}>★</div>
                <span>{t.about.f2}</span>
              </div>
              <div className="about-feature">
                <div className="icon" style={{ background: 'rgba(139,92,246,0.12)', color: '#8b5cf6' }}>♦</div>
                <span>{t.about.f3}</span>
              </div>
              <div className="about-feature">
                <div className="icon" style={{ background: 'rgba(244,114,182,0.12)', color: '#f472b6' }}>♥</div>
                <span>{t.about.f4}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="ecosystem">
        <div className="section-header">
          <div className="section-tag">{t.ecosystem.tag}</div>
          <h2 className="section-title">
            <span>{t.ecosystem.title1}</span>{' '}
            <span className="gradient-text">{t.ecosystem.title2}</span>
          </h2>
          <p className="section-subtitle">{t.ecosystem.subtitle}</p>
        </div>
        <div className="eco-grid">
          <div className="eco-card">
            <div className="eco-icon">◇</div>
            <h3>{t.ecosystem.base.title}</h3>
            <p>{t.ecosystem.base.desc}</p>
            <ul>
              {t.ecosystem.base.items.map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          </div>
          <div className="eco-card">
            <div className="eco-icon">✦</div>
            <h3>{t.ecosystem.aggregation.title}</h3>
            <p>{t.ecosystem.aggregation.desc}</p>
            <ul>
              {t.ecosystem.aggregation.items.map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          </div>
          <div className="eco-card">
            <div className="eco-icon">♦</div>
            <h3>{t.ecosystem.finance.title}</h3>
            <p>{t.ecosystem.finance.desc}</p>
            <ul>
              {t.ecosystem.finance.items.map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          </div>
          <div className="eco-card">
            <div className="eco-icon">♥</div>
            <h3>{t.ecosystem.entertainment.title}</h3>
            <p>{t.ecosystem.entertainment.desc}</p>
            <ul>
              {t.ecosystem.entertainment.items.map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          </div>
        </div>
      </section>

      <section id="vision">
        <div className="section-header">
          <div className="section-tag">{t.vision.tag}</div>
          <h2 className="section-title">
            <span>{t.vision.title1}</span>{' '}
            <span className="gradient-text">{t.vision.title2}</span>
          </h2>
          <p className="section-subtitle">{t.vision.subtitle}</p>
        </div>
        <div className="timeline">
          {t.vision.items.map((item, i) => (
            <div key={i} className="timeline-item">
              <div className="tl-content">
                <div className="tl-year">{2017 + i * 2}</div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="technology">
        <div className="section-header">
          <div className="section-tag">{t.technology.tag}</div>
          <h2 className="section-title">
            <span>{t.technology.title1}</span>{' '}
            <span className="gradient-text">{t.technology.title2}</span>{' '}
            <span>{t.technology.title3}</span>
          </h2>
          <p className="section-subtitle">{t.technology.subtitle}</p>
        </div>
        <div className="tech-grid">
          {t.technology.items.map((item, i) => (
            <div key={i} className="tech-card">
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;
